export const useAdminPublish = () => {
  const { $axios } = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();
  const toast = useToast();
  const publishingKey = ref("");
  const publishJobs = ref({});
  const pollTimers = new Map();

  const isProduction = computed(
    () => runtimeConfig.public.customEnv === "production"
  );
  const canShowPublish = computed(() => !isProduction.value);


  const publishStatusQuery = computed(() =>
    canShowPublish.value ? { withPublishStatus: 1 } : {}
  );

  const getPublishStatus = (row) => row?.publish_status || null;

  const isActiveJob = (job) => ["queued", "running"].includes(job?.status);

  const normalizeJob = (job, fallbackKey = "") => {
    if (!job?.id) return null;
    const total = Number(job.total || 0);
    const processed = Number(job.processed || 0);
    return {
      ...job,
      key: job.key || fallbackKey || `${job.resource}:job:${job.id}`,
      total,
      processed,
      created: Number(job.created || 0),
      updated: Number(job.updated || 0),
      skipped: Number(job.skipped || 0),
      failed: Number(job.failed || 0),
      errors: Array.isArray(job.errors) ? job.errors : [],
      results: Array.isArray(job.results) ? job.results : [],
    };
  };

  const setJob = (job, fallbackKey = "") => {
    const normalized = normalizeJob(job, fallbackKey);
    if (!normalized) return null;
    publishJobs.value = {
      ...publishJobs.value,
      [normalized.key]: normalized,
    };
    if (isActiveJob(normalized)) {
      publishingKey.value = normalized.key;
    }
    return normalized;
  };

  const getPublishProgress = (key) => publishJobs.value[key] || null;

  const getPublishProgressText = (key) => {
    const job = getPublishProgress(key);
    if (!job || !isActiveJob(job)) return "";
    return `${job.processed || 0}/${job.total || 0}`;
  };

  const getPublishButtonState = (row, options = {}) => {
    const {
      isNew = false,
      isModified = false,
      isBusy = false,
      isInvalid = false,
      fallbackLabel = "上线",
      key = "",
    } = options;

    const progressText = key ? getPublishProgressText(key) : "";

    if (!canShowPublish.value) {
      return { label: fallbackLabel, disabled: true, color: "success", title: "" };
    }
    if (isNew || isModified || !row?.id) {
      return {
        label: "未保存",
        disabled: true,
        color: "neutral",
        title: "请先保存到测试环境",
      };
    }
    if (isBusy || isInvalid) {
      return {
        label: isInvalid ? "需修正" : progressText || fallbackLabel,
        disabled: true,
        color: isInvalid ? "warning" : "success",
        title: isInvalid ? "请先修正表单错误并保存" : "",
      };
    }

    const status = getPublishStatus(row);
    if (status?.state === "published") {
      return {
        label: "已上线",
        disabled: true,
        color: "neutral",
        title: "当前测试数据与线上一致",
      };
    }
    if (status?.state === "unknown") {
      return {
        label: "状态异常",
        disabled: true,
        color: "warning",
        title: status.error || "线上状态获取失败",
      };
    }

    return {
      label: status?.label || fallbackLabel,
      disabled: status ? !status.can_publish : false,
      color: "success",
      title: status?.error || "",
    };
  };

  const normalizeIds = (ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    return list
      .map((id) => (id === null || id === undefined ? "" : String(id).trim()))
      .filter(Boolean);
  };

  const formatResult = (data) => {
    const result = data?.data || data || {};
    const created = Number(result.created || 0);
    const updated = Number(result.updated || 0);
    const skipped = Number(result.skipped || 0);
    const failed = Number(result.failed || 0);
    const parts = [];
    if (created) parts.push(`新增 ${created} 项`);
    if (updated) parts.push(`更新 ${updated} 项`);
    if (skipped) parts.push(`跳过 ${skipped} 项`);
    if (failed) parts.push(`失败 ${failed} 项`);
    return parts.length ? parts.join("，") : "生产环境数据已同步";
  };

  const getRequestOptions = (options = {}) => {
    const { confirm, confirmMessage, reload, ...requestOptions } = options;
    return Object.fromEntries(
      Object.entries(requestOptions).filter(([, value]) => typeof value !== "function")
    );
  };

  const showJobErrors = (job) => {
    const errors = Array.isArray(job?.errors) ? job.errors : [];
    if (!errors.length) return;
    const lines = errors
      .slice(0, 20)
      .map((item) => `${item?.id || "未知 ID"}: ${item?.message || item?.error || item}`)
      .join("\n");
    const suffix = errors.length > 20 ? `\n... 还有 ${errors.length - 20} 项失败` : "";
    if (import.meta.client && typeof window !== "undefined") {
      window.alert(`${job.status === "partial" ? "部分上线失败" : "上线失败"}\n\n${lines}${suffix}`);
    }
  };

  const fetchJob = async (jobId) => {
    const res = await $axios({
      method: "get",
      url: `sharkfoto/admin/publish-jobs/${jobId}`,
      baseURL: runtimeConfig.public.apiBase,
      timeout: 30 * 1000,
    });
    return res?.data?.data || res?.data;
  };

  const stopPolling = (jobId) => {
    const entry = pollTimers.get(jobId);
    if (entry?.timer) clearInterval(entry.timer);
    pollTimers.delete(jobId);
  };

  const handleFinalJob = async (job, { reload } = {}) => {
    stopPolling(job.id);
    if (publishingKey.value === job.key) publishingKey.value = "";
    const failed = Number(job.failed || 0);
    toast.add({
      title: failed ? "部分上线失败" : "上线成功",
      description: formatResult(job),
      color: failed ? "warning" : "success",
    });
    if (failed) showJobErrors(job);
    if (typeof reload === "function") await reload();
    return job;
  };

  const pollJob = (job, { reload } = {}) => {
    if (!job?.id) return Promise.resolve(null);
    if (pollTimers.has(job.id)) {
      const entry = pollTimers.get(job.id);
      if (typeof reload === "function") {
        entry.promise.then((finalJob) => {
          if (finalJob) reload();
        });
      }
      return entry.promise;
    }

    let resolvePolling;
    const promise = new Promise((resolve) => {
      resolvePolling = resolve;
    });
    const tick = async () => {
      try {
        const nextJob = setJob(await fetchJob(job.id), job.key);
        if (!nextJob) return;
        if (!isActiveJob(nextJob)) {
          const finalJob = await handleFinalJob(nextJob, { reload });
          resolvePolling(finalJob);
        }
      } catch (err) {
        stopPolling(job.id);
        if (publishingKey.value === job.key) publishingKey.value = "";
        toast.add({
          title: "上线状态获取失败",
          description: err?.message || "请刷新后重试",
          color: "error",
        });
        resolvePolling(null);
      }
    };
    const timer = setInterval(tick, 2000);
    pollTimers.set(job.id, { timer, promise });
    tick();
    return promise;
  };

  const restoreActiveJobs = async (resource, { reload } = {}) => {
    if (!canShowPublish.value) return [];
    try {
      const res = await $axios({
        method: "get",
        url: "sharkfoto/admin/publish-jobs",
        baseURL: runtimeConfig.public.apiBase,
        timeout: 30 * 1000,
        params: {
          status: "active",
          ...(resource ? { resource } : {}),
        },
      });
      const jobs = Array.isArray(res?.data?.data) ? res.data.data : [];
      jobs.forEach((job) => {
        const key = job?.mode === "item" && Array.isArray(job.ids) && job.ids.length === 1
          ? `${job.resource}:${job.ids[0]}`
          : `${job.resource}:batch`;
        const normalized = setJob(job, key);
        if (normalized && isActiveJob(normalized)) pollJob(normalized, { reload });
      });
      return jobs;
    } catch (err) {
      return [];
    }
  };

  const confirmPublish = ({ mode, ids, options }) => {
    if (options.confirm === false) return true;
    if (!import.meta.client || typeof window === "undefined") return true;
    const count = Array.isArray(ids) ? ids.length : 0;
    const message =
      options.confirmMessage ||
      (mode === "item" && count > 1
        ? `确认上线选中的 ${count} 项到生产环境？`
        : "确认上线到生产环境？");
    return window.confirm(message);
  };

  const publish = async ({ resource, mode, ids, key, options = {} }) => {
    if (isProduction.value) {
      toast.add({
        title: "无法上线",
        description: "生产环境不允许发起上线操作",
        color: "warning",
      });
      return null;
    }

    const normalizedIds = mode === "table" ? [] : normalizeIds(ids);
    if (mode !== "table" && !normalizedIds.length) {
      toast.add({
        title: "无法上线",
        description: "请先保存到测试环境，生成唯一 ID 后再上线",
        color: "warning",
      });
      return null;
    }

    if (!confirmPublish({ mode, ids: normalizedIds, options })) {
      return null;
    }

    const activeKey = key || `${resource}:${mode}:${normalizedIds.join(",")}`;
    publishingKey.value = activeKey;
    try {
      const requestOptions = getRequestOptions(options);
      const res = await $axios({
        method: "post",
        url: "sharkfoto/admin/publish-jobs",
        baseURL: runtimeConfig.public.apiBase,
        timeout: 30 * 1000,
        data: {
          resource,
          mode,
          ...(normalizedIds.length ? { ids: normalizedIds } : {}),
          ...(Object.keys(requestOptions).length ? { options: requestOptions } : {}),
        },
      });

      const result = setJob(res?.data?.data || res?.data, activeKey);
      toast.add({
        title: "已提交上线任务",
        description: result?.total ? `发布中 ${result.processed || 0}/${result.total}` : "发布任务已开始",
        color: "success",
      });
      if (typeof options.onSubmitted === "function" && result) {
        options.onSubmitted(result);
      }
      if (result) return await pollJob(result, { reload: options.reload });
      return null;
    } catch (err) {
      if (publishingKey.value === activeKey) publishingKey.value = "";
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "上线失败";
      toast.add({
        title: "上线失败",
        description: msg,
        color: "error",
      });
      return null;
    } finally {
      if (!publishingKey.value) publishingKey.value = "";
    }
  };

  const publishItem = (resource, id, key, options) => {
    return publish({ resource, mode: "item", ids: id, key, options });
  };

  const publishItems = (resource, ids, key, options) => {
    return publish({ resource, mode: "item", ids, key, options });
  };


  const createPublishBatch = ({
    resource,
    key,
    getRows,
    getRowId = (row) => row?.id,
    getRowKey = (row) => row?.rowKey || row?.id,
    canPublishRow,
    reload,
  }) => {
    const enabled = ref(false);
    const selectedKeys = ref(new Set());

    const rows = computed(() => (typeof getRows === "function" ? getRows() || [] : []));
    const candidateRows = computed(() =>
      rows.value.filter((row) => {
        const id = normalizeIds(getRowId(row))[0];
        return !!id && (!canPublishRow || canPublishRow(row));
      })
    );
    const selectedRows = computed(() =>
      candidateRows.value.filter((row) => selectedKeys.value.has(String(getRowKey(row))))
    );
    const selectedCount = computed(() => selectedRows.value.length);
    const candidateCount = computed(() => candidateRows.value.length);

    const start = () => {
      selectedKeys.value = new Set();
      enabled.value = true;
    };

    const cancel = () => {
      selectedKeys.value = new Set();
      enabled.value = false;
    };

    const isSelected = (row) => selectedKeys.value.has(String(getRowKey(row)));

    const toggle = (row, checked) => {
      const next = new Set(selectedKeys.value);
      const rowKey = String(getRowKey(row));
      if (checked) {
        if (!canPublishRow || canPublishRow(row)) next.add(rowKey);
      } else {
        next.delete(rowKey);
      }
      selectedKeys.value = next;
    };

    const selectAll = () => {
      selectedKeys.value = new Set(candidateRows.value.map((row) => String(getRowKey(row))));
    };

    const confirm = async () => {
      const ids = selectedRows.value
        .map((row) => normalizeIds(getRowId(row))[0])
        .filter(Boolean);
      if (!ids.length) {
        toast.add({
          title: "无法批量上线",
          description: "请先选择可上线的数据",
          color: "warning",
        });
        return null;
      }
      return await publishItems(resource, ids, key || `${resource}:batch`, {
        confirmMessage: `确认上线选中的 ${ids.length} 项到生产环境？`,
        reload,
        onSubmitted: cancel,
      });
    };

    if (import.meta.client) {
      onMounted(() => {
        restoreActiveJobs(resource, { reload });
      });
    }

    return {
      enabled,
      selectedCount,
      candidateCount,
      start,
      cancel,
      selectAll,
      confirm,
      isSelected,
      toggle,
    };
  };

  if (import.meta.client) {
    onMounted(() => {
      restoreActiveJobs();
    });
    onBeforeUnmount(() => {
      pollTimers.forEach((entry) => {
        if (entry?.timer) clearInterval(entry.timer);
      });
      pollTimers.clear();
    });
  }

  return {
    publishingKey,
    isProduction,
    canShowPublish,
    publishStatusQuery,
    getPublishStatus,
    getPublishButtonState,
    getPublishProgress,
    getPublishProgressText,
    restoreActiveJobs,
    createPublishBatch,
    publish,
    publishItem,
    publishItems,
  };
};
